const en = {
  common: {
    actions: {
      previous: "Previous",
      next: "Next",
      showPrevious: "Show previous items",
      showNext: "Show next items",
      openMenu: "Open navigation menu",
      changeLanguage: "Change language",
    },
    labels: {
      language: "Language",
    },
    cta: {
      contact: "Contact us",
      learnMore: "Learn more",
      exploreApproach: "Explore our approach",
      submit: "Submit",
    },
    forms: {
      firstName: "First name",
      lastName: "Last name",
      email: "Email",
      phoneNumber: "Phone number",
      message: "Message",
      emailPlaceholder: "you@company.com",
      phonePlaceholder: "+244 000 000 000",
      intlPhonePlaceholder: "+1 (555) 000-0000",
      messagePlaceholder: "Leave us a message...",
    validation: {
        firstNameRequired: "First name is required.",
        lastNameRequired: "Last name is required.",
        emailRequired: "Email is required.",
        invalidEmail: "Please enter a valid email address.",
        phoneRequired: "Phone number is required.",
        invalidPhone: "Please enter a valid phone number.",
        messageRequired: "Message is required.",
      },
    },
    feedback: {
      sending: "Sending...",
      messageSent: "Your message has been sent successfully.",
      somethingWrong: "Something went wrong. Please try again.",
      serverError: "Server error. Please try again later.",
    },
    siteCta: {
      title: "Our strategic partner to transform Angola",
      text: "Working with governments, investors, and institutions to unlock strategic opportunities and support sustainable economic transformation in Angola.",
    },
    company: {
      shortDescription: "Working with governments, investors, and institutions to unlock strategic opportunities and support sustainable economic transformation in Angola.",
      locationLuanda: "Rua Frederico Welwitschia Torre Maculusso, 5th Floor Luanda, Angola",
      locationCapeTown: "The Estuaries, Building 12 Century City Cape Town, 7441",
    },
    footer: {
      quickLinks: "Quick links",
      servicesTitle: "Our services",
      phoneLabel: "Phone No:",
      copyright: "© 2027 Provest Capital Management. All rights reserved.",
      links: {
        home: "Home",
        about: "About",
        services: "Services",
        newsroom: "Newsroom",
        contact: "Contact us",
      },
      services: [
        "Public & Civil Works",
        "Residential Projects",
        "Industrial Infrastructure",
        "Rehabilitation & Renovation",
        "Project Management & Supervision",
      ],
      legal: {
        privacy: "Privacy Policy",
        terms: "Terms & Conditions",
      },
    },
  },
  header: {
    nav: {
      home: "Home",
      about: "About us",
      ourPosition: "Our Position",
      esg: "ESG",
      contact: "Contact us",
      solutions: "Solutions",
      privateSector: "Private sector",
      publicSector: "Public sector",
    },
  },
  pages: {
    home: {
      hero: {
        title: "Strategy & problem solving for high-impact projects",
      },
      moment: {
        title: "Angola at a decisive moment",
        subtitle: "The potential for diversification is vast, but complexity demands more than capital alone: it requires method and execution.",
        cards: [
          {
            title: "Economic Diversification",
            text: "Urgent transition from an oil-dependent economy to productive and diversified sectors. Building sustainable economic foundations.",
          },
          {
            title: "Smart Capital",
            text: "Sophisticated financial structuring to enable long-term projects. Strategic investments that create sustainable value.",
          },
          {
            title: "Disciplined Execution",
            text: "Ability to navigate the operational context and ensure effective implementation. Strong governance and accountability frameworks.",
          },
        ],
      },
      positioning: {
        title: "Our positioning",
        subtitle: "Strategy + Problem Solving: solid strategic thinking combined with real execution capability",
        strategyTitle: "Strategy",
        strategyText: "In-depth strategic analysis of the economic, institutional and sectoral context to:",
        strategyPoints: [
          "Clarify critical priorities of each project",
          "Identify real value-creation opportunities",
          "Design structured solutions that support informed investment decisions",
        ],
        problemTitle: "Problem Solving",
        problemText: "Hands-on support to implementation, navigating real-world project complexity:",
        problemPoints: [
          "Overcoming institutional and operational obstacles",
          "Effective coordination between stakeholders",
          "Ensuring disciplined execution from structuring to operation",
        ],
      },
      who: {
        title: "Who we are",
        subtitle: "An Angolan company specialised in project structuring, strategic management and capital mobilisation.",
        missionTitle: "Our mission",
        missionText: "To support companies and institutions in creating sustainable value and accessing strategic capital, through an approach grounded in transparency, professionalism and responsibility.",
        visionTitle: "Our vision",
        visionText: "To become a reference partner in capital mobilisation for structured projects that drive Angola's real economy through lasting alignment between strategy, capital and execution.",
      },
      operating: {
        title: "Our way of operating",
        subtitle: "We operate where strategy, capital and execution converge.",
        cards: [
          {
            id: "01",
            title: "Strategy & Modelling",
            points: [
              "Definition of strategic direction and economic viability",
              "Rigorous risk management to support informed investment decisions",
            ],
          },
          {
            id: "02",
            title: "Structuring & Capital",
            points: [
              "Financial structuring and capital mobilisation with banks and funds",
              "Financial modelling aligned with the project's scale and risk profile",
            ],
          },
          {
            id: "03",
            title: "Technical Partnerships",
            points: [
              "Selection and integration of leading technical and operational partners",
              "Effective transfer of know-how and best practices",
            ],
          },
          {
            id: "04",
            title: "Execution & Navigation",
            points: [
              "Continuous monitoring and institutional coordination",
              "Full alignment between strategy, capital and execution",
            ],
          },
        ],
      },
      solutions: {
        title: "Our solutions",
        subtitle: "We deliver tailored strategies across sectors to drive sustainable growth and competitive advantage",
        items: [
          {
            title: "Private sector",
            desc: "Strategic and execution-focused solutions that help businesses expand, optimize performance, and achieve sustainable growth in competitive markets.",
          },
          {
            title: "Public sector",
            desc: "Advisory and implementation support for governments and institutions to develop impactful projects, strengthen infrastructure, and drive long-term economic progress.",
          },
        ],
      },
      esg: {
        title: "ESG as an execution lever",
        subtitle: "Integrating environmental, social, and governance principles into project execution to ensure long-term sustainability and responsible development.",
        items: [
          {
            title: "Environmental Responsibility",
            desc: "Commitment to environmentally responsible practices.",
          },
          {
            title: "Social Impact",
            desc: "Projects designed to generate meaningful social benefits.",
          },
          {
            title: "Governance Transparency",
            desc: "Strong governance standards ensuring accountability.",
          },
          {
            title: "Sustainable Investment Frameworks",
            desc: "Investment strategies aligned with sustainability principles.",
          },
        ],
      },
    },
    about: {
      hero: {
        title: "Built for Angola's decisive moment",
        text: "A company born from conviction that Angola's potential requires more than ambition. It demands strategy, discipline, and execution rooted in deep local knowledge.",
      },
      stats: [
        { value: "10+", label: "Years of expertise" },
        { value: "40+", label: "Projects executed" },
        { value: "$2B+", label: "Capital structured" },
        { value: "12", label: "Sectors covered" },
        { value: "5", label: "Countries of operation" },
      ],
      story: {
        badgeLabel: "PART OF",
        badgeTitle: "PROVEST HOLDING",
        badgeText: "Headquartered in South Africa",
        title: "Built at the intersection of strategy, capital & execution",
        paragraphOne: "PRO-VEST Capital Management is an Angolan company specialised in project structuring, strategic management and capital mobilisation, combining the discipline of international finance with deep local market intelligence.",
        paragraphTwo: "We are part of PROVEST Holding, headquartered in South Africa, which strengthens our ability to execute real-economy projects in Angola by bringing international corporate structure and global capabilities to local execution capacity.",
        quote: "We don't just advise. We structure, capitalise, and execute while staying accountable throughout the entire project lifecycle.",
      },
      pillars: {
        title: "Three pillars of our approach",
        text: "Every engagement is built on the intersection of capital intelligence, operational rigour, and governance integrity.",
        items: [
          {
            id: "01",
            title: "Strategic Capital Intelligence",
            description: "We identify where smart capital creates multiplier effects, not just returns. Financial structuring aligned with Angola's macroeconomic realities and long-term development goals.",
            tags: ["Capital structuring", "Risk modelling", "Investment strategy"],
          },
          {
            id: "02",
            title: "Operational execution",
            description: "Strategy without execution is fiction. We stay present from conception through completion, navigating Angola's operational environment with discipline and accountability frameworks.",
            tags: ["Project management", "PMO support", "KPI monitoring"],
          },
          {
            id: "03",
            title: "Governance & transparency",
            description: "Trust is our operating model. Every structure we build embeds transparency, accountability, and responsible investment principles, creating sustainable value for all stakeholders.",
            tags: ["ESG integration", "Compliance", "Reporting"],
          },
        ],
      },
      who: {
        title: "Who we are",
        subtitle: "An Angolan company specialised in project structuring, strategic management and capital mobilisation.",
        missionTitle: "Our mission",
        missionText: "To support companies and institutions in creating sustainable value and accessing strategic capital, through an approach grounded in transparency, professionalism and responsibility.",
        visionTitle: "Our vision",
        visionText: "To become a reference partner in capital mobilisation for structured projects that drive Angola's real economy through lasting alignment between strategy, capital and execution.",
      },
      values: {
        title: "Values that guide every decision",
        text: "Built on strong principles, our values shape our culture and drive consistent excellence, helping us deliver trusted, impactful, and future-focused solutions.",
        items: [
          {
            title: "Accountability without compromise",
            description: "We are measured by outcomes, not effort. Every project we lead comes with a clear accountability structure and we hold ourselves to it before asking the same of others.",
            featured: true,
          },
          {
            title: "Integrity as a business model",
            description: "Transparency is not compliance. It is competitive advantage. Our reputation is our most durable asset.",
          },
          {
            title: "Local first, global standard",
            description: "We operate with the intimacy of a local partner and the rigour of a global institution.",
          },
          {
            title: "Long-term thinking",
            description: "We optimise for decade-level value creation, not quarterly optics. Angola's transformation is generational.",
          },
          {
            title: "Disciplined innovation",
            description: "We bring fresh thinking to old problems, always anchored in evidence, feasibility, and the realities of execution on the ground.",
          },
        ],
      },
      document: {
        title: "ANPG provisional registration certificate",
        text: "View PVMT-PROVEST Capital Management's provisional registration certificate for Angola's oil and gas sector, issued by the National Oil, Gas and Biofuels Agency.",
        fallback: "This browser cannot display the PDF inline.",
        open: "Open PDF",
      },
    },
    ourPosition: {
      hero: {
        title: "Solid strategic thinking combined with real execution capability",
        text: "We sit at the intersection of strategy and execution, not as advisors who leave after the deck, but as partners who stay through the complexity and deliver measurable outcomes.",
      },
      positioning: {
        title: "Our positioning",
        text: "Strategy + problem solving: solid strategic thinking combined with real execution capability",
        keyCapabilities: "Key capabilities",
        sections: [
          {
            eyebrow: "Strategy",
            title: "In-depth strategic planning for smarter outcomes.",
            description: "We analyze market dynamics, institutional context, and project goals to build a strong foundation for success.",
            capabilities: [
              "Identify key priorities and growth opportunities",
              "Align strategy with business and financial objectives",
              "Develop structured, data-backed execution plans",
              "Enable informed, confident decision-making",
            ],
          },
          {
            eyebrow: "Problem solving",
            title: "Practical solutions that turn challenges into progress.",
            description: "We provide hands-on support to navigate complexities and ensure seamless execution from planning to delivery.",
            capabilities: [
              "Overcome operational and structural challenges",
              "Align stakeholders and streamline processes",
              "Drive efficient execution with clear accountability",
              "Ensure consistency from strategy to implementation",
            ],
          },
        ],
      },
      convergence: {
        title: "Where strategy, capital and execution converge",
        text: "We operate at the convergence of three forces: strategic clarity, smart capital, and disciplined execution to deliver real-economy outcomes.",
        techStrip: "Technology & data intelligence: Smarter risk analysis with AI-driven insights.",
        cards: [
          {
            id: "01",
            title: "Strategy & modelling",
            description: "Connects strategy, analysis and decision-making. Rigorous risk analysis to support the best investment choice and long-term value creation for all stakeholders.",
          },
          {
            id: "02",
            title: "Structuring & capital",
            description: "Structuring to gain access to capital with partners and investors. Financial modelling aligned with the project's scale, risk profile, and long-term sustainability.",
          },
          {
            id: "03",
            title: "Technical partnerships",
            description: "Select and integrate leading technical partners to access strategic capital, through an approach that is anchored in international best practices and local knowledge.",
          },
          {
            id: "04",
            title: "Execution & navigation",
            description: "Continuous monitoring and institutional coordination. Full alignment between strategy, capital and execution throughout the entire operation from first draw to final delivery.",
          },
        ],
      },
      differentiators: {
        title: "Our competitive differentiators",
        text: "Five reasons why PRO-VEST is the right strategic partner for complex Angola projects. Hover each row to explore.",
        items: [
          {
            id: "01",
            title: "Execution-first, always",
            description: "We are not a consulting firm that leaves after the presentation. We stay through the complexity from structuring to operation, maintaining full accountability for outcomes. Our business model is built on delivery, not advice.",
            badge: "Core Edge",
          },
          {
            id: "02",
            title: "Local intelligence",
            description: "Deep Angola market knowledge that cannot be replicated by firms operating from the outside. We know the institutions, the dynamics, and the path through complexity. Local insight is our primary competitive advantage.",
            badge: "Market",
          },
          {
            id: "03",
            title: "International standards",
            description: "Backed by PROVEST Holding in South Africa, we bring international corporate structure and a global network that elevate every engagement to international standards of rigour and governance. Local execution, global standards.",
            badge: "Network",
          },
          {
            id: "04",
            title: "ESG as an edge",
            description: "We integrate Environmental, Social and Governance principles into project design not as compliance, but as a strategic lever that unlocks better capital, better partners, and longer-lasting results for every stakeholder.",
            badge: "Sustainability",
          },
          {
            id: "05",
            title: "Proven track record",
            description: "Over $2B in capital structured, 40+ projects executed, and a decade of building credibility with governments, institutions, and international investors in Angola and the region. The numbers speak for themselves.",
            badge: "Track record",
          },
        ],
      },
      stats: [
        { value: "10+", label: "Years delivering in Angola's real economy" },
        { value: "40+", label: "Projects executed in private & public sectors" },
        { value: "$2B+", label: "Capital structured across all projects" },
        { value: "5", label: "Countries of active operation" },
      ],
    },
    esg: {
      hero: {
        title: "ESG as an Execution Lever",
        text: "We integrate environmental, social and governance principles into project design and implementation, ensuring long-term sustainability and responsible development.",
        pillarsLabel: "ESG pillars",
        pillars: [
          { key: "E", label: "Environmental" },
          { key: "S", label: "Social" },
          { key: "G", label: "Governance" },
        ],
      },
      stats: [
        { value: "100%", label: "Projects with ESG framework" },
        { value: "3", label: "ESG pillars integrated" },
        { value: "10+", label: "Years of responsible investing" },
        { value: "4", label: "Countries covered" },
      ],
      intro: {
        kicker: "ESG as execution lever",
        title: "We embed ESG into every project and decision.",
        caption: "At a time when markets demand both performance and responsibility, ESG is not a reporting checkbox for us — it is a strategic lever that shapes how capital is deployed, how projects are structured, and how outcomes are delivered. We integrate environmental, social, and governance principles from the earliest stages of planning through to execution and oversight, ensuring that every decision is aligned with long-term value creation.",
        caption1: "By embedding ESG into our core processes, we strengthen risk management, enhance transparency, and build trust with stakeholders. This approach enables us to unlock more resilient investments, foster stronger partnerships, and deliver projects that are not only commercially sound but also socially responsible and environmentally sustainable.",
        paragraphs: [
          "Angola's decisive moment requires capital that is not only smart but responsible. ESG is not a reporting checkbox for us. It is a strategic lever that unlocks capital, strengthens partnerships, and delivers project outcomes.",
          "We integrate environmental, social and governance principles into project design and implementation, ensuring long-term sustainability and responsible development across our mandates.",
        ],
        quote: "ESG as an execution lever means integrating environmental, social and governance principles into project execution to ensure long-term sustainability.",
      },
      environmental: {
        title: "Environmental responsibility",
        text: "Committed to environmentally responsible practices across every project we structure and execute in Angola and the region.",
        badgeLabel: "Environmental - E",
        badgeText: "Projects with environmental impact assessment",
        items: [
          {
            title: "Environmental impact assessment",
            description: "Every project includes a comprehensive environmental review before structuring begins, covering risks, mitigation, and long-term sustainability metrics.",
          },
          {
            title: "Responsible practices integration",
            description: "We align projects with environmentally responsible practices so delivery contributes positively to Angola's natural environment and international standards.",
          },
          {
            title: "Climate-aligned capital",
            description: "Capital is directed toward projects that support resilience, diversification, and measurable environmental transition goals.",
          },
        ],
      },
      social: {
        title: "Social impact",
        text: "Projects designed to generate meaningful social benefits through employment, resilience, and stronger local participation.",
        panelLabel: "Social impact - S",
        panelTitle: "Community first",
        panelText: "Every project evaluated for social return",
        items: [
          {
            title: "Employment creation",
            description: "Every project we structure is evaluated for direct and indirect job creation, prioritising meaningful and sustainable roles for Angolan workers.",
          },
          {
            title: "Community resilience",
            description: "We back projects that strengthen local capacity, improve access to services, and reduce vulnerability through practical community outcomes.",
          },
          {
            title: "Livelihood improvement",
            description: "Beyond infrastructure, we look for measurable social benefit through access to economic opportunity, essential services, and long-term inclusion.",
          },
        ],
      },
      governance: {
        title: "Governance transparency",
        text: "Strong governance standards ensure accountability, transparent reporting, and responsible institutional behaviour.",
        items: [
          {
            title: "Transparency standards",
            description: "Strong governance standards ensure accountability across all mandates, with reporting and disclosure built into the project structure from the outset.",
          },
          {
            title: "Compliance frameworks",
            description: "Comprehensive compliance frameworks align with international governance standards so every project meets investor and development finance requirements.",
          },
          {
            title: "Accountability structures",
            description: "Clear accountability structures protect the interests of investors, governments, communities, and operators throughout the execution lifecycle.",
          },
        ],
      },
    },
    contact: {
      hero: {
        title: "Get in touch with our experts",
        text: "Every project starts with a conversation. Let's discuss your vision and translate it into a practical, high-impact solution.",
      },
      intro: {
        eyebrow: "Get in touch",
        title: "Let's build something impactful",
        text: "Whether you're structuring an investment, seeking government advisory, or exploring how PRO-VEST can be your Angola partner, we want to hear from you.",
      },
      contactItems: [
        {
          title: "Phone",
          primary: "+244 938 888 888",
          secondary: "+244 961 765 500",
        },
        {
          title: "Email",
          primary: "info@provest.co.ao",
          secondary: "We reply within 24 hours",
        },
        {
          title: "LinkedIn",
          primary: "PRO-VEST Capital Management",
          secondary: "Connect with our team",
        },
      ],
      locations: {
        eyebrow: "Our locations",
        title: "Where we operate",
        text: "Permanently based in Angola with presence in South Africa, not remote advisors, but embedded partners in the markets we serve.",
        items: [
          {
            id: "ao",
            tab: "AO Luanda, Angola",
            title: "Luanda, Angola - Primary Office",
            address: "Rua Frederico Welwitschia, Torre Maculusso 5th Floor - Luanda, Angola",
            mapTitle: "Luanda office map",
          },
          {
            id: "za",
            tab: "ZA Cape Town, SA",
            title: "Cape Town, South Africa - PROVEST Holding",
            address: "The Estuaries, Building 12, Century City Cape Town, 7441",
            mapTitle: "Cape Town office map",
          },
        ],
      },
    },
    privateSector: {
      hero: {
        title: "Private sector solutions",
        text: "Strategic and execution-focused solutions that help businesses expand, optimise performance, and achieve sustainable growth in competitive markets.",
        chipsLabel: "Private sector service areas",
        chips: ["Investment & Expansion", "M&A & Capital"],
      },
      expansion: {
        title: "Investment & expansion",
        text: "Growth-led projects and new investments from greenfield opportunities to full corporate transformation.",
        cards: [
          {
            eyebrow: "Investments",
            title: "Greenfield projects and new investments",
            description: "We identify, structure, and execute greenfield investment opportunities from financial feasibility through to project delivery. Our team brings together the capital, partners, and execution capability needed to turn opportunities into operating assets.",
            tags: ["Viability", "Structuring", "Execution"],
          },
          {
            eyebrow: "Structure",
            title: "Corporate growth, expansion and transformation",
            description: "Supporting established businesses to scale, expand into new markets, or transform their operating model through strategic advisory, capital structuring, and hands-on execution support that goes beyond the boardroom.",
            tags: ["Growth", "Expansion", "Transformation"],
          },
        ],
      },
      capital: {
        title: "M&A & capital",
        text: "Mergers, acquisitions and capital restructuring — navigating complexity to unlock strategic value.",
        cards: [
          {
            eyebrow: "M&A",
            title: "Mergers and acquisitions (M&A)",
            description: "End-to-end M&A advisory — from target identification and valuation through to negotiation, due diligence, and transaction closing. We navigate Angola's unique regulatory and institutional environment to deliver outcomes that advisory firms working remotely cannot.",
            tags: ["Valuation", "Negotiation", "Closing"],
          },
          {
            eyebrow: "Capital & Financial",
            title: "Capital restructuring and financial optimisation",
            description: "Redesigning the capital structure of businesses to unlock growth, reduce financial risk, and attract new investment — through equity restructuring, debt refinancing, and financial modelling aligned with the company's strategic objectives and Angola's capital market realities.",
            tags: ["Restructuring", "Financial modelling", "Optimisation"],
          },
        ],
      },
      disciplines: {
        title: "Where strategy meets four disciplines",
        text: "From strategic planning to execution, our integrated capabilities ensure clarity, efficiency, and long-term success across every project.",
        items: [
          {
            number: "01",
            title: "Strategy",
            description: "Tracks strategic trends of the economic and institutional context to clarify priorities and create real investment-grade decisions.",
          },
          {
            number: "02",
            title: "Financial structuring",
            description: "Design the right capital stack aligned with project scale, risk profile, and investor requirements from feasibility through to bankability.",
          },
          {
            number: "03",
            title: "Right partners",
            description: "Select and integrate the right technical, commercial, and institutional partners using a best-practice selection methodology.",
          },
          {
            number: "04",
            title: "Execution discipline",
            description: "Continuous monitoring, institutional coordination, and full alignment between strategy, capital and execution throughout the entire operation.",
          },
        ],
      },
      contact: {
        title: "Transform your ideas into results",
        text: "From concept to execution, we help businesses create meaningful impact through tailored, high-performance solutions.",
      },
      cta: {
        title: "Ready to grow your private sector business?",
        text: "Whether you are launching a greenfield investment, pursuing an acquisition, or restructuring for growth, our team is ready to structure and execute.",
      },
    },
    publicSector: {
      hero: {
        title: "Public sector solutions",
        text: "Advisory and representation support for governments and institutions to develop impactful projects, strengthen infrastructure, and drive long-term economic progress.",
        chipsLabel: "Public sector service areas",
        chips: ["Structuring projects", "PPP & concession models"],
      },
      projects: {
        title: "Structuring projects",
        text: "Large-scale economic development projects turning complex public investment priorities into bankable, executable programmes.",
        cards: [
          {
            eyebrow: "DEVELOPMENT",
            title: "Large-scale economic development projects",
            description: "We structure large-scale investment programmes on behalf of governments and institutions from feasibility through financial structuring to execution oversight. Our team navigates Angola's institutional environment to unlock capital that would otherwise remain inaccessible.",
            tags: ["Planning", "Structuring", "Execution"],
          },
          {
            eyebrow: "INVESTMENT",
            title: "Investment programmes with territorial and sectoral impact",
            description: "Designing investment programmes that deliver territorial and sectoral impact, creating lasting infrastructure, economic diversification, and development outcomes aligned with Angola's national priorities and provincial development plans.",
            tags: ["Territorial", "Sectoral", "Impact"],
          },
        ],
      },
      capital: {
        "title": "M&A & capital",
        "text": "Mergers, acquisitions and capital restructuring \u2014 navigating complexity to unlock strategic value.",
        "cards": [
          {
            "eyebrow": "PPP",
            "title": "Structuring of public-private partnerships (PPP)",
            "description": "We design and structure PPP frameworks that align public sector objectives with private sector incentives \u2014 creating structures that attract institutional investors, development finance institutions, and international capital to Angola's priority projects.",
            "tags": [
              "PPP design",
              "Risk allocation",
              "Investor structuring"
            ]
          },
          {
            "eyebrow": "Capital & Financial",
            "title": "Capital restructuring and financial optimisation",
            "description": "Redesigning the capital structure of businesses to unlock growth, reduce financial risk, and attract new investment \u2014 through equity restructuring, debt refinancing, and financial modelling aligned with the company's strategic objectives and Angola's capital market realities.",
            "tags": [
              "PPP",
              "Risk",
              "Bankability"
            ]
          }
        ]
      },
      ppp: {
        title: "PPP & concession models",
        text: "Structuring Public-Private Partnerships that make public sector projects bankable, fundable, and executable.",
        cards: [
          {
            eyebrow: "PPP",
            title: "Structuring of public-private partnerships (PPP)",
            description: "We design and structure PPP frameworks that align public sector objectives with private sector incentives, creating structures that attract institutional investors, development finance institutions, and international capital to Angola's priority projects.",
            tags: ["PPP design", "Risk allocation", "Investor structuring"],
          },
          {
            eyebrow: "Concession Models",
            title: "Concession models, risk sharing and bankability",
            description: "Designing concession models that distribute risk appropriately between public and private parties, creating structures that meet the bankability requirements of lenders while protecting the public interest and delivering sustainable outcomes for Angola's communities.",
            tags: ["PPP", "Risk", "Bankability"],
          },
        ],
      },
      disciplines: {
        title: "Four pillars of public sector delivery",
        text: "From strategic planning to execution, our integrated capabilities ensure clarity, efficiency, and long-term success across every project.",
      },
      contact: {
        title: "Transform your ideas into results",
        text: "From concept to execution, we help public institutions create meaningful impact through tailored, high-performance solutions.",
      },
      cta: {
        title: "Ready to structure your next public sector project?",
        text: "Whether you are a government ministry, provincial institution, or a multilateral seeking a trusted Angola partner to structure and execute, we are ready.",
      },
    },
    privacy: {
      title: "Privacy Policy",
      text: "This policy explains how we collect, use, protect, and manage information when you use our website or engage with our team.",
      lastUpdated: "Last updated: April 23, 2026",
      lead: "PRO-VEST Capital Management respects privacy and handles information with care. This page provides a simple overview of our general privacy practices.",
      sections: [
        {
          title: "Information we collect",
          text: "We may collect contact details, company information, project enquiries, correspondence, and technical information such as browser, device, and website usage data when you interact with PRO-VEST Capital Management.",
        },
        {
          title: "How we use information",
          text: "We use information to respond to enquiries, evaluate potential engagements, provide advisory or project-related services, improve our website, maintain business records, and protect the security and integrity of our operations.",
        },
        {
          title: "Sharing information",
          text: "We do not sell personal information. We may share information with trusted professional advisers, service providers, project partners, regulators, or authorities where required for legitimate business, legal, compliance, or operational purposes.",
        },
        {
          title: "Data protection",
          text: "We apply reasonable administrative, technical, and organisational safeguards designed to protect information against unauthorised access, loss, misuse, or alteration.",
        },
        {
          title: "Your choices",
          text: "You may contact us to request access, correction, or deletion of personal information where applicable. Some information may be retained where required for legal, contractual, audit, or legitimate business reasons.",
        },
        {
          title: "International operations",
          text: "Because our work may involve Angola, South Africa, and international partners, information may be processed across borders where necessary for business operations and project execution.",
        },
      ],
      updatesTitle: "Contact and updates",
      updatesText: "If you have questions about this Privacy Policy, please contact us through the contact page. We may update this policy from time to time to reflect operational, legal, or website changes.",
    },
    terms: {
      title: "Terms & Conditions",
      text: "These terms govern your access to and use of this website and its content.",
      lastUpdated: "Last updated: April 23, 2026",
      lead: "By using this website, you accept these Terms & Conditions. If you do not agree with them, please do not use the website.",
      sections: [
        {
          title: "Use of this website",
          text: "This website is provided for general information about PRO-VEST Capital Management, our capabilities, and our areas of work. You agree to use the website responsibly and only for lawful purposes.",
        },
        {
          title: "No professional advice",
          text: "Website content is not financial, investment, legal, tax, or professional advice. Any engagement with PRO-VEST Capital Management is subject to separate written agreements and agreed scopes of work.",
        },
        {
          title: "Intellectual property",
          text: "All text, graphics, branding, layouts, and other website materials are owned by or licensed to PRO-VEST Capital Management unless otherwise stated. You may not copy, modify, or reuse materials without permission.",
        },
        {
          title: "Accuracy of information",
          text: "We aim to keep website information useful and accurate, but we do not guarantee that all content is complete, current, or free from errors. We may update or remove content at any time.",
        },
        {
          title: "Third-party links",
          text: "The website may reference third-party sites, tools, or services. We are not responsible for third-party content, availability, policies, or practices.",
        },
        {
          title: "Limitation of liability",
          text: "To the fullest extent permitted by applicable law, PRO-VEST Capital Management is not liable for losses arising from use of, inability to use, or reliance on this website or its content.",
        },
      ],
      updatesTitle: "Changes to these terms",
      updatesText: "We may revise these Terms & Conditions from time to time. Continued use of this website after updates means you accept the revised terms.",
    },
    banner: {
      title: "Website is Under Construction",
      text: "Our website is currently under construction as we work to enhance your experience. If you have any inquiries, please feel free to contact us via email or phone. Thank you for your patience.",
      callUs: "Call us",
      location: "Location",
    },
  },
};

export default en;
